import communicationInit from '../../shared/communication-module'
import {
  iCall,
  iPathObj,
} from '../../shared/communication-module/types'
import i18nInit, {
  changeLanguage,
  pluginLocalStorage,
} from '../../shared/i18n-module'
import { iChangeLanguage } from '../../shared/i18n-module/change-language'
import {
  iEnvironmentFile,
  iI18nConfig,
  iI18nConfigApi,
  iLanguage,
  iRequestLang,
} from '../../shared/i18n-module/types'
import authModule, {
  getAuthData,
  logout,
  refresh,
} from '../../shared/oauth2-auth-code-flow-pkce'
import { iGetAuthData } from '../../shared/oauth2-auth-code-flow-pkce/get-auth-data'
import { iAuthInit } from '../../shared/oauth2-auth-code-flow-pkce/init'
import { iLogout } from '../../shared/oauth2-auth-code-flow-pkce/logout'
import { Irefresh } from '../../shared/oauth2-auth-code-flow-pkce/refresh'
import { urlUtils } from '../../shared/oauth2-auth-code-flow-pkce/utils'
import { getCookieByName } from '../../shared/utils'
import {
  COMUNNICATION_CONFIG_LANGUAGE,
  ENDPOINTS_CONFIG_FILE_NAME,
  ENVIRONMENTS,
  ENVIRONMENT_FILE_NAME,
} from './constants'
import { getStaticJson } from './get-static-json'
import { iEndpointConfig } from './types'

type IpartialI18nConfig = Omit<iI18nConfig, 'requestLang'>

export interface iMediatorConfig {
  i18nConfig: IpartialI18nConfig
  communicationConfig: iEndpointConfig
  enableAuth?: boolean
}

export interface iMediatorApi {
  i18n: {
    langCode: iI18nConfigApi['langCode']
    language: iI18nConfigApi['language']
    changeLanguage: iChangeLanguage
  }
  call: iCall
  api: iPathObj
  auth: {
    getData: iGetAuthData
    init: iAuthInit
    logout: iLogout
    refresh: Irefresh
  }
  env: string
}

const init = async (
  arqConfig: iMediatorConfig,
): Promise<iMediatorApi> => {
  let environment
  // environment config
  try {
    // obtengo el entorno si es que existe el fichero
    const { env } = await getStaticJson<iEnvironmentFile>(
      `//${location.host}/${ENVIRONMENT_FILE_NAME}`,
    )
    environment = env

    // se obtiene el archivo de configuración de endpoints para el entorno
    const endpointsObj = await getStaticJson<iEndpointConfig>(
      `//${location.host}/${ENDPOINTS_CONFIG_FILE_NAME[environment]}`,
    )

    arqConfig.communicationConfig = endpointsObj
  } catch (e) {
    console.log(e)
    // Si no se ha pódido obtener el archivo de entorno, suponemos que
    // estamos en entorno "LOCAL" y se usa la configuración de arqConfig que se
    // le ha pasado desde la aplicación
    environment = ENVIRONMENTS.LOCAL
  }

  const { call, api } = await communicationInit(
    arqConfig.communicationConfig,
  )

  // auth module initialization

  // Se intenta recuperar el código de idioma de varios sitios, la
  // preferencia es
  // 1. queryParam "lang"  (requisito de cliente)
  // 2. cookie "lang"      (se establece en keycoak)
  // 3. localStorage "lang" (de una ejecución anterior elegida por el usuario)
  // 4. initialLanguage (si se informa)
  const langQueryParam = urlUtils.getQueryParam('lang')
  urlUtils.clearParam('lang')
  const langCookieParam = getCookieByName('lang')
  const i18nStore = pluginLocalStorage()
  const langUserSelectedParam = i18nStore.get()

  const initialLanguage =
    langQueryParam ||
    langCookieParam ||
    langUserSelectedParam ||
    arqConfig.i18nConfig.initialLanguage ||
    null

  if (initialLanguage !== null) {
    const domain = location.host
      .slice(location.host.indexOf('.') + 1)
      .split(':')[0]

    document.cookie = [
      `lang=${initialLanguage};`,
      `domain=${domain};`,
      `samesite=none;`,
      `path=/;`,
      `secure=false`,
    ].join('')

    i18nStore.put(initialLanguage)
  }

  if (arqConfig.enableAuth) {
    await authModule({
      ...arqConfig.communicationConfig.authConfig,
      additionalParams: {
        ...arqConfig.communicationConfig.authConfig.additionalParams,
        ...(initialLanguage !== null
          ? { ui_locales: initialLanguage }
          : undefined),
      },
    })
  }

  // i18n-module initialization
  const getLanguage: iRequestLang = async (lang: string) => {
    if (call.api.language?.get === undefined)
      throw new Error(COMUNNICATION_CONFIG_LANGUAGE)

    const { data } = await call.api.language.get<iLanguage, any>({
      lang,
    })
    return data
  }

  const i18nApi = await i18nInit({
    ...arqConfig.i18nConfig,
    requestLang: getLanguage,
    ...(initialLanguage !== null ? { initialLanguage } : undefined),
    store: i18nStore,
  })

  return {
    i18n: {
      ...i18nApi,
      changeLanguage,
    },
    call,
    api,
    auth: {
      getData: getAuthData,
      init: authModule,
      logout,
      refresh,
    },
    env: environment,
  }
}

export default init
